import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  apiLogin, apiLogout, apiMfaVerify, apiRegister, apiRequest, isMfaChallenge, loadSession,
  saveSession, setExpiredHandler, setMfaRequiredHandler,
  type MfaChallenge, type RegisterInput, type Session,
} from '../lib/api';

/**
 * 30 minutes with no interaction ends the session client-side, matching the
 * server-side idle check in rotateRefreshToken (src/domain/tokens.ts) — that
 * check alone would only ever surface as a confusing error on someone's next
 * click, sometime later. This is what makes it a visible, deliberate sign-out
 * instead.
 *
 * Only deliberate interaction resets the clock — click, key, scroll, touch,
 * wheel — not bare mouse movement, which fires continuously and would make
 * the timer meaningless (a mouse merely resting near the trackpad is not a
 * person still using the app).
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'] as const;

export interface ProviderSignupInput {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  acceptedTerms: true;
  /** Who is posting property adverts (156). */
  advertiserRole?: 'owner' | 'agent' | 'caretaker' | 'individual';
}

interface AuthValue {
  session: Session | null;
  isAuthenticated: boolean;
  roles: string[];
  /**
   * Whether this person holds a permission, e.g. can('appointments.create').
   *
   * The codes come from the SERVER, on /api/v1/me, resolved from the same
   * grants that admit or refuse the request itself. The web deliberately keeps
   * no copy of the permission matrix: a role list hard-coded here would be a
   * second source of truth, and the first grant anybody changed would leave it
   * quietly wrong — showing a button that 403s, or hiding one that works.
   *
   * It decides what to OFFER and nothing else. Every route is still gated
   * server-side; hiding a control is courtesy, not security, and a client that
   * lied to itself about this would gain a 403 and nothing more.
   *
   * Unknown until /me answers. It returns FALSE while loading, so a control
   * appears once it is known to work rather than flickering away a moment
   * after the page paints.
   */
  can: (permission: string) => boolean;
  /**
   * Returns the session it just created.
   *
   * The sign-in screen has to decide where to send someone the instant they
   * authenticate, and there are now three answers — the landlord app, the
   * Control Center, the Tenant Portal. Reading `roles` off this context to make
   * that choice does not work: the state set here has not re-rendered by the
   * time the caller's next line runs, so it would route on the PREVIOUS
   * session, which for a fresh sign-in is no session at all. Handing the
   * session back is the difference between routing on what is true and routing
   * on what was true a moment ago.
   */
  /** Creates an Explore posting account and signs them in. */
  signUpToPost: (input: ProviderSignupInput) => Promise<Session>;
  /**
   * Either a session, or a challenge: when a second factor is due the
   * password alone earns nothing but a five-minute token for the second step,
   * and nothing is stored until that step succeeds.
   */
  login: (identifier: string, password: string) => Promise<Session | MfaChallenge>;
  /** The second step. On success the session is stored exactly as login's would be. */
  completeMfa: (mfaToken: string, code: string) => Promise<Session>;
  /** Adopts a session minted elsewhere — after enrolment confirms, the server
   *  hands back tokens that carry the second-factor proof. */
  adoptSession: (s: Session) => void;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const navigate = useNavigate();

  // A refresh failure deep inside the API layer clears the session here, so the
  // whole app falls back to the sign-in screen the moment the tokens die.
  useEffect(() => {
    setExpiredHandler(() => setSession(null));
    // A platform session without a second factor can reach nothing in the
    // Control Centre; the only place it can go is enrolment.
    setMfaRequiredHandler(() => navigate('/security/two-factor', { replace: true }));
  }, [navigate]);

  const apply = useCallback((s: Session) => {
    saveSession(s);
    setSession(s);
  }, []);

  const login = useCallback(async (identifier: string, password: string) => {
    const r = await apiLogin(identifier, password);
    // A challenge is not a session: store nothing, hand it to the screen.
    if (isMfaChallenge(r)) return r;
    apply(r);
    return r;
  }, [apply]);

  const completeMfa = useCallback(async (mfaToken: string, code: string) => {
    const s = await apiMfaVerify(mfaToken, code);
    apply(s);
    return s;
  }, [apply]);

  const adoptSession = useCallback((s: Session) => { apply(s); }, [apply]);

  const register = useCallback(async (input: RegisterInput) => {
    apply(await apiRegister(input));
  }, [apply]);

  /**
   * Signing up to post in Explore (§11).
   *
   * Its own call rather than register(): that one creates a landlord account
   * with a portfolio and a subscription, and somebody listing their plumbing
   * business would end up owning an empty one. Session handling stays here so
   * there is one place that decides what being signed in means.
   */
  const signUpToPost = useCallback(async (input: ProviderSignupInput) => {
    const s = await apiRequest<Session>('/api/v1/public/services/signup', {
      method: 'POST', body: input,
    });
    apply(s);
    return s;
  }, [apply]);

  /*
   * Fetched once per session rather than read from the token: a role or grant
   * changed by an administrator takes effect on the next request server-side,
   * and this follows on the next sign-in or reload. Failure is silent and
   * leaves the set empty, which hides optional controls rather than offering
   * ones that would be refused.
   */
  const [permissions, setPermissions] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!session) { setPermissions(null); return; }
    let live = true;
    apiRequest<{ permissions?: string[] }>('/api/v1/me')
      .then((r) => { if (live) setPermissions(new Set(r.permissions ?? [])); })
      .catch(() => { if (live) setPermissions(new Set()); });
    return () => { live = false; };
  }, [session]);

  const can = useCallback(
    (permission: string) => permissions?.has(permission) ?? false, [permissions]);

  const logout = useCallback(async () => {
    const current = loadSession();
    if (current) await apiLogout(current.refreshToken);
    saveSession(null);
    setSession(null);
  }, []);

  useEffect(() => {
    if (!session) return;
    let timer: ReturnType<typeof setTimeout>;
    const onIdle = () => {
      void logout().then(() => navigate('/login', { replace: true, state: { reason: 'idle' } }));
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(onIdle, IDLE_TIMEOUT_MS);
    };
    reset();
    for (const evt of ACTIVITY_EVENTS) window.addEventListener(evt, reset, { passive: true });
    return () => {
      clearTimeout(timer);
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, reset);
    };
  }, [session, logout, navigate]);

  const value = useMemo<AuthValue>(() => ({
    session,
    isAuthenticated: !!session,
    roles: session?.roles ?? [],
    can,
    signUpToPost,
    login,
    completeMfa,
    adoptSession,
    register,
    logout,
  }), [session, can, signUpToPost, login, completeMfa, adoptSession, register, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
